import { isAdmin, isCustomerAdmin, isRoot, projectFeatureEnabled } from "./helpers";

const DEVS = ["2", "1672", "2075", "1785"];

export interface AdminMayInterface {
  // admin/customers
  (session: SessionInterface, path: "admin/customers"): boolean;
  (session: SessionInterface, path: "admin/customers", action: "new"): boolean;
  (
    session: SessionInterface,
    path: "admin/customers",
    action: "edit" | "delete",
    customer: CustomerRefWithIdentifier,
  ): boolean;

  // admin/projects
  (session: SessionInterface, path: "admin/projects"): boolean;
  (session: SessionInterface, path: "admin/projects", action: "new"): boolean;
  (session: SessionInterface, path: "admin/projects", action: "edit" | "delete", project: ProjectRef): boolean;

  // admin/project/call-results
  (session: SessionInterface, path: "admin/project/call-results"): boolean;
  (session: SessionInterface, path: "admin/project/call-results", action: "new"): boolean;
  (
    session: SessionInterface,
    path: "admin/project/call-results",
    action: "edit" | "delete",
    result: HistoriesCallResultType,
  ): boolean;

  // admin/project/contact-status
  (session: SessionInterface, path: "admin/project/contact-status"): boolean;
  (session: SessionInterface, path: "admin/project/contact-status", action: "new"): boolean;
  (
    session: SessionInterface,
    path: "admin/project/contact-status",
    action: "edit" | "delete",
    status: ContactStatusRef,
  ): boolean;

  // admin/users
  (session: SessionInterface, path: "admin/users"): boolean;
  (session: SessionInterface, path: "admin/users", action: "new"): boolean;
  (
    session: SessionInterface,
    path: "admin/users",
    action: "edit" | "delete" | "api_token" | "disableOtp",
    user: UserRef,
  ): boolean;

  // admin/computers
  (session: SessionInterface, path: "admin/computers"): boolean;
  (session: SessionInterface, path: "admin/computers", action: "new"): boolean;
  (session: SessionInterface, path: "admin/computers", action: "edit" | "delete", project: ComputerInterface): boolean;

  // admin/utilities
  (session: SessionInterface, path: "admin/utilities"): boolean;
  (
    session: SessionInterface,
    path: "admin/utilities",
    action:
      | "regenContactStates"
      | "regenSearchIndex"
      | "usersOnline"
      | "jobs"
      | "recalculatePoints"
      | "cacheDebug"
      | "workstationsOnline"
      | "regenFinanceReports",
  ): boolean;

  // admin/worktimes/tasks
  (session: SessionInterface, path: "admin/worktimes/tasks"): boolean;
  (session: SessionInterface, path: "admin/worktimes/tasks", action: "new"): boolean;
  (
    session: SessionInterface,
    path: "admin/worktimes/tasks",
    action: "edit" | "delete",
    task: WorktimeTaskInterface,
  ): boolean;

  // admin/profiles
  (session: SessionInterface, path: "admin/profiles"): boolean;
  (session: SessionInterface, path: "admin/profiles", action: "new"): boolean;
  (session: SessionInterface, path: "admin/profiles", action: "edit" | "delete", task: ProfileRef): boolean;

  // admin/teams
  (session: SessionInterface, path: "admin/teams"): boolean;
  (session: SessionInterface, path: "admin/teams", action: "new"): boolean;
  (session: SessionInterface, path: "admin/teams", action: "edit" | "delete", task: TeamRef): boolean;

  // admin/communications
  (session: SessionInterface, path: "admin/communications"): boolean;
  (session: SessionInterface, path: "admin/communications", action: "new"): boolean;
  (session: SessionInterface, path: "admin/communications", action: "edit" | "delete", task: CommunicationRef): boolean;

  // admin/communication-lists
  (session: SessionInterface, path: "admin/communication-lists"): boolean;
  (session: SessionInterface, path: "admin/communication-lists", action: "new"): boolean;
  (
    session: SessionInterface,
    path: "admin/communication-lists",
    action: "edit" | "delete",
    task: CommunicationListRef,
  ): boolean;

  // admin/feastdayl
  (session: SessionInterface, path: "admin/feastdays"): boolean;
  (session: SessionInterface, path: "admin/feastdays", action: "new"): boolean;
  (session: SessionInterface, path: "admin/feastdays", action: "edit" | "delete", task: FeastdayRef): boolean;

  // admin/reminders
  (session: SessionInterface, path: "admin/reminders"): boolean;
  (session: SessionInterface, path: "admin/reminders", action: "new"): boolean;
  (session: SessionInterface, path: "admin/reminders", action: "edit" | "delete", task: ReminderRef): boolean;

  // admin/mail-templates
  (session: SessionInterface, path: "admin/mail-templates"): boolean;
  (session: SessionInterface, path: "admin/mail-templates", action: "new"): boolean;
  (session: SessionInterface, path: "admin/mail-templates", action: "edit" | "delete", task: MailTemplateRef): boolean;

  // admin/mail-attachments
  (session: SessionInterface, path: "admin/mail-attachments"): boolean;
  (session: SessionInterface, path: "admin/mail-attachments", action: "new"): boolean;
  (
    session: SessionInterface,
    path: "admin/mail-attachments",
    action: "edit" | "delete",
    task: MailTemplateAttachmentRef,
  ): boolean;

  // admin/api-keys
  (session: SessionInterface, path: "admin/api-keys"): boolean;
  (session: SessionInterface, path: "admin/api-keys", action: "new" | "revoke"): boolean;

  // admin/secrets
  (session: SessionInterface, path: "admin/secrets"): boolean;
  (session: SessionInterface, path: "admin/secrets", action: "new" | "edit" | "delete" | "decrypt"): boolean;

  // admin/oauth2
  (session: SessionInterface, path: "admin/oauth2"): boolean;
  (session: SessionInterface, path: "admin/oauth2", action: "new"): boolean;
  (session: SessionInterface, path: "admin/oauth2", action: "edit" | "delete", task: OauthTokenRef): boolean;

  // admin/su
  (session: SessionInterface, path: "admin/su"): boolean;

  // admin/audit
  (session: SessionInterface, path: "admin/audit"): boolean;
  (session: SessionInterface, path: "admin/audit", action: "show", entry: AuditLogEntryRef): boolean;

  // admin/worktime-audit
  (session: SessionInterface, path: "admin/worktimes-audit"): boolean;
  (session: SessionInterface, path: "admin/worktimes-audit", action: "show", entry: AuditLogEntryRef): boolean;

  (session: SessionInterface, path: "admin"): boolean;
}

const AdminPermissions: PermissionType<any> = {
  "admin/customers": {
    index: ({ currentUser }, _resource) => isAdmin(currentUser),
    delete: ({ currentUser }, customer) => isAdmin(currentUser) && customer.identifier !== "termitel",
  },
  "admin/projects": {
    index: ({ currentUser, currentCustomer }, _resource) => isCustomerAdmin(currentUser, currentCustomer),
  },
  "admin/project/call-results": {
    index: ({ currentUser, currentCustomer }, _resource) => isCustomerAdmin(currentUser, currentCustomer),
    edit: ({ currentUser, currentCustomer }, _resource) => isCustomerAdmin(currentUser, currentCustomer),
    delete: ({ currentUser, currentCustomer }, _resource) => isCustomerAdmin(currentUser, currentCustomer),
  },
  "admin/project/contact-status": {
    index: ({ currentUser, currentCustomer }, _resource) => isCustomerAdmin(currentUser, currentCustomer),
    edit: ({ currentUser, currentCustomer }, _resource) => isCustomerAdmin(currentUser, currentCustomer),
    delete: ({ currentUser, currentCustomer }, _resource) => isCustomerAdmin(currentUser, currentCustomer),
  },
  "admin/users": {
    index: ({ currentUser, currentCustomer }, _resource) => isCustomerAdmin(currentUser, currentCustomer),
    api_token: ({ currentUser }, user) => isRoot(currentUser) && user.active,
    disableOtp: ({ currentUser }, user) => isRoot(currentUser) && user.otp,
  },
  "admin/computers": {
    index: ({ currentUser, currentCustomer }, _resource) =>
      parseInt(currentCustomer.id, 10) === 1 && isCustomerAdmin(currentUser, currentCustomer),
  },
  "admin/utilities": {
    index: ({ currentUser }, _resource) => DEVS.includes(currentUser.id),
    regenFinanceReports: ({ currentUser }, _resource) => ["2", "3", "6"].includes(currentUser.id),
  },
  "admin/worktimes/tasks": {
    index: ({ currentUser, currentCustomer }, _resource) =>
      isCustomerAdmin(currentUser, currentCustomer) && projectFeatureEnabled(currentCustomer, "worktimes"),
  },
  "admin/profiles": {
    index: ({ currentUser, currentCustomer }, _resource) => isCustomerAdmin(currentUser, currentCustomer),
  },
  "admin/teams": {
    index: ({ currentUser, currentCustomer }, _resource) =>
      isRoot(currentUser) ||
      (isCustomerAdmin(currentUser, currentCustomer) && currentUser.customerId !== currentCustomer.id),
  },
  "admin/communications": {
    index: ({ currentUser, currentCustomer }, _resource) => isCustomerAdmin(currentUser, currentCustomer),
  },
  "admin/communication-lists": {
    index: ({ currentUser, currentCustomer }, _resource) => isCustomerAdmin(currentUser, currentCustomer),
  },
  "admin/feastdays": {
    index: ({ currentUser, currentCustomer }, _resource) => isCustomerAdmin(currentUser, currentCustomer),
  },
  "admin/reminders": {
    index: ({ currentUser, currentCustomer }, _resource) => isCustomerAdmin(currentUser, currentCustomer),
  },
  "admin/mail-templates": {
    index: ({ currentUser, currentCustomer, currentProject }, _resource) =>
      isCustomerAdmin(currentUser, currentCustomer) && projectFeatureEnabled(currentProject, "emails"),
  },
  "admin/mail-attachments": {
    index: ({ currentUser, currentCustomer, currentProject }, _resource) =>
      isCustomerAdmin(currentUser, currentCustomer) && projectFeatureEnabled(currentProject, "emails"),
  },

  "admin/api-keys": {
    index: ({ currentUser }, _resource) => isRoot(currentUser),
  },

  "admin/secrets": {
    index: ({ currentUser }, _resource) => isRoot(currentUser),
  },

  "admin/oauth2": {
    index: ({ currentUser, currentCustomer }, _resource) => isCustomerAdmin(currentUser, currentCustomer),
    edit: ({ currentUser }, _resource) => isAdmin(currentUser),
    delete: ({ currentUser }, _resource) => isAdmin(currentUser),
  },

  "admin/su": {
    index: ({ currentUser, currentCustomer }, _resource) => isCustomerAdmin(currentUser, currentCustomer),
  },

  "admin/audit": {
    index: ({ currentUser }, _resource) => isRoot(currentUser),
  },

  "admin/worktimes-audit": {
    index: ({ currentUser }, _resource) => isRoot(currentUser),
  },

  admin: {
    index: ({ currentUser, currentCustomer }, _resource) => isCustomerAdmin(currentUser, currentCustomer),
  },
};

export default AdminPermissions;
